import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, User, Star, Shield, LogOut, Mail, Smartphone, Check, MessageSquare, FileText, Bell, Trash2, Loader2, Sparkles, Gift } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import RatingDialog from "@/components/RatingDialog";
import IOSInstallGuide from "@/components/IOSInstallGuide";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import DeleteAccountFlow from "@/components/DeleteAccountFlow";
import { useTierGate } from "@/hooks/useTierGate";
import { PaywallModal } from "@/components/PaywallModal";
import PlusBadge from "@/components/PlusBadge";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const Settings = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { isPlus } = useTierGate();
  const { toast } = useToast();
  const [firstName, setFirstName] = useState("");
  const [ratingOpen, setRatingOpen] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [stats, setStats] = useState({ contactCount: 0, messagesGenerated: 0, reflectionLikes: 0, accountAgeMonths: 0 });
  const pwa = usePWAInstall();

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("first_name, created_at")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.first_name) setFirstName(data.first_name);
        if (data?.created_at) {
          const months = Math.floor((Date.now() - new Date(data.created_at).getTime()) / (1000 * 60 * 60 * 24 * 30));
          setStats(s => ({ ...s, accountAgeMonths: months }));
        }
      });
    supabase.from("contacts").select("id", { count: "exact", head: true }).eq("user_id", user.id)
      .then(({ count }) => setStats(s => ({ ...s, contactCount: count || 0 })));
    supabase.from("message_generation_logs").select("id", { count: "exact", head: true }).eq("user_id", user.id)
      .then(({ count }) => setStats(s => ({ ...s, messagesGenerated: count || 0 })));
    supabase.from("reflection_engagement").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("liked", true)
      .then(({ count }) => setStats(s => ({ ...s, reflectionLikes: count || 0 })));
  }, [user]);

  const initial = firstName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "?";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      if (error || !data?.url) throw new Error('Failed');
      window.location.href = data.url;
    } catch {
      toast({ title: 'Something went wrong', description: 'Please try again.', variant: 'destructive' });
      setPortalLoading(false);
    }
  };

  // Build menu items
  type MenuItem = {
    icon: typeof User;
    label: string;
    description: string;
    onClick: () => void;
    disabled?: boolean;
    installed?: boolean;
  };

  const items: MenuItem[] = [
    {
      icon: User,
      label: "My Profile",
      description: "Name and birthday",
      onClick: () => navigate("/settings/profile"),
    },
    {
      icon: Bell,
      label: "My Preferences",
      description: "Reminders and notifications",
      onClick: () => navigate("/settings/preferences"),
    },
  ];

  // App Experience (mobile only)
  if (!pwa.isDesktop) {
    items.push({
      icon: Smartphone,
      label: "App Experience",
      description: pwa.isInstalled
        ? "You're already using the app experience"
        : "Add Dearestly to your home screen",
      onClick: pwa.isInstalled
        ? () => {}
        : pwa.isIOS
          ? () => setIosGuideOpen(true)
          : () => pwa.promptInstall(),
      disabled: pwa.isInstalled,
      installed: pwa.isInstalled,
    });
  }

  items.push(
    {
      icon: Gift,
      label: "Gift orders",
      description: "View your Dearestly gift orders",
      onClick: () => navigate("/orders"),
    },
    {
      icon: MessageSquare,
      label: "Feedback",
      description: "Rate the app or get in touch",
      onClick: () => setFeedbackOpen(true),
    },
    {
      icon: FileText,
      label: "Legal",
      description: "Privacy Notice and Terms of Use",
      onClick: () => navigate("/settings/legal"),
    },
    {
      icon: Trash2,
      label: "Delete Account",
      description: "Permanently remove your account and data",
      onClick: () => setDeleteOpen(true),
    },
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4 md:px-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-page-title text-foreground">Settings</h1>
            <div className="mt-0.5 h-0.5 w-10 rounded-full bg-accent" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8 md:px-6 animate-fade-in">
        {/* User section */}
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-accent text-2xl font-bold text-accent-foreground shadow-md">
            {initial}
          </div>
          <p className="mt-3 text-section-heading text-foreground">
            {firstName || "Welcome"}
          </p>
          <p className="text-muted-sm">{user?.email}</p>
        </div>

        {/* Subscription section */}
        <div className="mb-8 card-premium p-5">
          {isPlus ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-card-heading text-foreground">Dearestly Plus</span>
                <PlusBadge />
              </div>
              <p className="text-muted-sm">
                {profile?.subscription_granted_by === 'admin'
                  ? 'Complimentary'
                  : profile?.subscription_expires_at
                    ? `Renews ${format(new Date(profile.subscription_expires_at), 'd MMMM yyyy')}`
                    : 'Active'}
              </p>
              {profile?.subscription_granted_by !== 'admin' && (
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="mt-1 text-sm font-medium text-left hover:underline"
                  style={{ color: '#C9A84C' }}
                >
                  {portalLoading ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
                  ) : (
                    'Manage subscription →'
                  )}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <p className="text-muted-sm">You are on Dearestly Free.</p>
              <button
                onClick={() => setPaywallOpen(true)}
                className="text-sm font-medium text-left hover:underline"
                style={{ color: '#C9A84C' }}
              >
                Upgrade to Dearestly Plus →
              </button>
            </div>
          )}
        </div>

        {/* Menu items */}
        <div className="space-y-3">
          {items.map((item, index) => (
            <button
              key={item.label}
              onClick={item.onClick}
              disabled={item.disabled}
              style={{ animationDelay: `${index * 80}ms` }}
              className="card-premium flex w-full items-center gap-4 p-5 transition-shadow hover:shadow-md animate-fade-in disabled:opacity-70"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                <item.icon className="h-5 w-5 text-accent" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-card-heading text-foreground">{item.label}</p>
                <p className="text-muted-sm">{item.description}</p>
              </div>
              {item.installed ? (
                <Check className="h-5 w-5 shrink-0 text-accent" />
              ) : (
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />
              )}
            </button>
          ))}
        </div>

        {/* Upgrade card for free users */}
        {!isPlus && (
          <div
            onClick={() => setPaywallOpen(true)}
            style={{ animationDelay: `${items.length * 80}ms` }}
            className="mt-6 flex w-full cursor-pointer items-center gap-4 rounded-2xl border-2 border-accent bg-card p-5 shadow-sm transition-shadow hover:shadow-md animate-fade-in"
          >
            <div className="flex-1 text-left">
              <p className="text-card-heading text-foreground">Upgrade to Dearestly Plus</p>
              <p className="text-muted-sm">Unlimited contacts, all occasions, and more.</p>
            </div>
            <Sparkles className="h-5 w-5 shrink-0 text-accent" />
          </div>
        )}

        {/* Sign out */}
        <button
          onClick={handleSignOut}
          style={{ animationDelay: `${(items.length + 1) * 80}ms` }}
          className="card-premium mt-3 flex w-full items-center gap-4 p-5 transition-shadow hover:shadow-md animate-fade-in"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
            <LogOut className="h-5 w-5 text-destructive" />
          </div>
          <p className="flex-1 text-left text-card-heading text-destructive">Sign out</p>
        </button>
      </main>

      {/* Feedback bottom sheet */}
      <Drawer open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DrawerContent>
          <DrawerHeader className="text-center">
            <DrawerTitle className="font-serif text-xl">Feedback</DrawerTitle>
          </DrawerHeader>
          <div className="flex flex-col gap-3 px-6 pb-8">
            <button
              onClick={() => {
                setFeedbackOpen(false);
                setTimeout(() => setRatingOpen(true), 300);
              }}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                <Star className="h-5 w-5 text-accent" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-foreground">Rate Dearestly</p>
                <p className="text-sm text-muted-foreground">Let us know how we're doing</p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />
            </button>
            <a
              href="https://wa.me/85244617799?text=Hi%20Dearestly!%20I%27d%20like%20to%20share%20some%20feedback"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                <Mail className="h-5 w-5 text-accent" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-foreground">Contact Us</p>
                <p className="text-sm text-muted-foreground">Chat with us on WhatsApp</p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />
            </a>
          </div>
        </DrawerContent>
      </Drawer>

      <RatingDialog open={ratingOpen} onOpenChange={setRatingOpen} />
      <IOSInstallGuide
        open={iosGuideOpen}
        onOpenChange={setIosGuideOpen}
        onDismiss={pwa.dismissIOSGuide}
      />
      <DeleteAccountFlow open={deleteOpen} onOpenChange={setDeleteOpen} stats={stats} />
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title="Unlock Dearestly Plus"
        benefit="Unlimited contacts, all occasion types, unlimited messages, and more."
      />
    </div>
  );
};

export default Settings;
